import { useCallback, useEffect, useState } from "react";
import type { Role } from "@xinghai/shared";

/** Priority when a user holds several roles — the first match is the home role. */
export const ROLE_PRIORITY: readonly Role[] = [
  "student",
  "annotator",
  "adjudicator",
  "teacher",
  "course_team",
  "admin",
];

export const ROLE_LABELS: Record<Role, string> = {
  student: "学生",
  annotator: "标注专家",
  adjudicator: "仲裁专家",
  teacher: "班级教师",
  course_team: "课程团队",
  admin: "管理员",
};

/** Landing route for a role. */
export function homeFor(role: Role | null | undefined): string {
  switch (role) {
    case "student":
      return "/s";
    case "annotator":
      return "/a";
    case "adjudicator":
      return "/j";
    case "teacher":
    case "course_team":
    case "admin":
      return "/m";
    default:
      return "/login";
  }
}

/** Last role the person used, when they still hold it. Otherwise the priority fallback. */
export function landingRole(roles: readonly Role[]): Role | null {
  const stored = read();
  if (stored && roles.includes(stored)) return stored;
  return primaryRole(roles);
}

export function primaryRole(roles: readonly Role[]): Role | null {
  for (const r of ROLE_PRIORITY) if (roles.includes(r)) return r;
  return roles[0] ?? null;
}

/** Roles sorted by priority, for the role switcher. */
export function sortRoles(roles: readonly Role[]): Role[] {
  return ROLE_PRIORITY.filter((r) => roles.includes(r));
}

const KEY = "xz.active_role";

function read(): Role | null {
  try {
    return (localStorage.getItem(KEY) as Role | null) ?? null;
  } catch {
    return null;
  }
}

/**
 * Which role the shell is currently presenting. Defaults to the highest
 * priority role the user holds; persisted so a multi-role user stays put.
 */
export function useActiveRole(roles: readonly Role[]): [Role | null, (role: Role) => void] {
  const [stored, setStored] = useState<Role | null>(() => read());

  useEffect(() => {
    if (stored && !roles.includes(stored)) setStored(null);
  }, [roles, stored]);

  const active = stored && roles.includes(stored) ? stored : primaryRole(roles);

  const setActive = useCallback((role: Role) => {
    setStored(role);
    try {
      localStorage.setItem(KEY, role);
    } catch {
      /* ignore */
    }
  }, []);

  return [active, setActive];
}
